import React, {useLayoutEffect, useState} from 'react';
import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Typography
} from "@material-tailwind/react";
import {NumericFormat} from "react-number-format";
import axios from "axios";
import LotSummary from "./lot_summary";

// Motivo 11 - ALTA POR FINCA SUSTRAIDA: alta de construcciones omitidas/no declaradas
// sobre una cuenta existente. Solo agrega construcciones nuevas (las actuales se
// muestran de solo lectura); el backend recalcula avalúo y regenera adeudos.
const ConstructionAddition = ({lot, movement}) => {
    const [error, setError] = useState(null);
    const [loaderActive, setLoaderActive] = useState(false);
    const [openEndDialog, setOpenEndDialog] = useState(false);
    const [currentLot, setCurrentLot] = useState([]);
    const [currentConstructions, setCurrentConstructions] = useState([]);
    const [newConstructions, setNewConstructions] = useState({});
    const [constValues, setConstValues] = useState([]);
    const [constDevaluations, setConstDevaluations] = useState([]);

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    const handleReset = () => {
        window.location.replace('movements');
    };

    useLayoutEffect(() => {
        setCurrentLot(lot);

        async function fetchData() {
            try {
                const [constructionsResponse, valuesResponse, devaluationsResponse] = await Promise.all([
                    fetch(`/lots/${lot.parcel_code}/constructions.json`),
                    fetch(`/construction_values.json`),
                    fetch(`/construction_devaluations.json`),
                ]);

                if (constructionsResponse.ok) {
                    const constructionsData = await constructionsResponse.json();
                    setCurrentConstructions(constructionsData);
                } else {
                    console.log("No se encontraron construcciones para este código.");
                }

                if (valuesResponse.ok) {
                    const constValuesData = await valuesResponse.json();
                    setConstValues(constValuesData);
                } else {
                    console.log("No se pudieron cargar valores de construcción.");
                }

                if (devaluationsResponse.ok) {
                    const devaluationsData = await devaluationsResponse.json();
                    setConstDevaluations(devaluationsData);
                } else {
                    console.log("No se pudieron cargar valores de devaluaciónes para construcciones.");
                }
            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }

        fetchData();
    }, [lot]);

    const addConstruction = () => {
        const newKey = Date.now();

        setNewConstructions((prev) => ({
            ...prev,
            [newKey]: {
                area: 0,
                construction_devaluation_id: 0,
                construction_value_id: null,
            },
        }));
    };

    const removeConstruction = (id) => {
        setNewConstructions((prev) => {
            const {[id]: _, ...rest} = prev;
            return rest;
        });
    };

    const handleNewConstructionChange = (id, field, value) => {
        setNewConstructions((prev) => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value,
            },
        }));
    };

    const validate = () => {
        const entries = Object.values(newConstructions);
        if (entries.length === 0) {
            return 'Agrega al menos una construcción.';
        }
        const invalid = entries.some((data) =>
            !data.construction_value_id || data.construction_value_id === "0" || Number(data.area) <= 0
        );
        if (invalid) {
            return 'Cada construcción necesita clave de valuación y superficie mayor a 0.';
        }
        return null;
    };

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);
        setLoaderActive(true);

        const constructions = Object.values(newConstructions).map((data) => ({
            area: data.area,
            construction_devaluation_id: data.construction_devaluation_id,
            construction_value_id: data.construction_value_id,
        }));

        try {
            const response = await axios.post(`lots/${lot.parcel_code}/revaluation.json`, {
                lot: {
                    notes: currentLot.notes,
                    reason_type_id: movement.id,
                    effective_bimester: currentLot.effective_bimester,
                    effective_year: currentLot.effective_year,
                },
                new_constructions: constructions,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    "X-CSRF-Token": getCSRFToken(),
                }
            });
            if (response.status === 200) {
                setOpenEndDialog(true);
                const current_lot_url = `/lots/${lot.parcel_code}/print_movement.pdf`;
                window.open(current_lot_url, '_blank');
                setLoaderActive(false);
            } else {
                setError('Error al dar de alta la construcción. Verifica los datos.');
                setLoaderActive(false);
            }
        } catch (err) {
            setError('Error al guardar. Verifica los datos.');
            setLoaderActive(false);
        }
    };

    const totalNewArea = Object.values(newConstructions).reduce((acc, construction) => acc + Number(construction.area || 0), 0);

    return (
        <>
            <div className="flex-auto py-6 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Información del predio
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div
                        className="flex-grow border border-blue-gray-100 p-4 rounded-t-none rounded-b-lg w-full lg:w-1/2 overflow-x-hidden">
                        <LotSummary lot={lot}/>
                    </div>
                </div>
            </div>
            <div className="flex-auto py-10 pt-0">
                <h6 className="font-semibold uppercase bg-blue-500 text-white p-2 rounded-t-lg">
                    Alta por finca sustraída
                </h6>
                <Card className='w-full border border-blue-gray-100 rounded-b-lg rounded-t-none' shadow={false}>
                    <CardBody className='p-4'>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                            CONSTRUCCIONES REGISTRADAS
                        </h6>
                        <table className="w-full min-w-[640px] table-auto border">
                            <thead>
                            <tr className='bg-gray-50'>
                                {["Cve. Valuación", "Edad", "Sup. Const."].map(
                                    (el) => (
                                        <th
                                            key={el}
                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                        >
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                {el}
                                            </Typography>
                                        </th>
                                    )
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {currentConstructions.length === 0 ? (
                                <tr className="w-full">
                                    <td colSpan="100%" className="text-center py-3">
                                        <strong>El predio no cuenta con construcciones.</strong>
                                    </td>
                                </tr>
                            ) : (currentConstructions.map((construction, index, array) => {
                                    const className = `py-3 px-5 ${
                                        index === array.length - 1 ? "" : "border-b border-blue-gray-50"
                                    }`;
                                    return (
                                        <tr key={construction.construction_id}>
                                            <td className={className}>{construction.construction_value_id}</td>
                                            <td className={className}>{construction.construction_devaluation_id}</td>
                                            <td className={className}>
                                                <NumericFormat
                                                    value={construction.area}
                                                    displayType={'text'}
                                                    thousandSeparator=","
                                                /> m<sup>2</sup>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                        </table>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-600 text-sm mt-3 mb-2 font-bold uppercase">
                            CONSTRUCCIONES NUEVAS
                        </h6>
                        <div className='flex justify-end mb-3'>
                            <Button variant="filled"
                                    onClick={addConstruction}
                                    className="bg-indigo-500 px-2 py-2 rounded">
                                + Agregar construcción
                            </Button>
                        </div>
                        <table className="w-full min-w-[640px] table-auto">
                            <thead>
                            <tr className='bg-gray-50'>
                                {["Cve. Valuación", "Edad", "Sup. Const.", ""].map(
                                    (el, index) => (
                                        <th
                                            key={index}
                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                        >
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                {el}
                                            </Typography>
                                        </th>
                                    )
                                )}
                            </tr>
                            </thead>
                            <tbody>
                            {Object.entries(newConstructions).length === 0 ? (
                                <tr className="w-full">
                                    <td colSpan="100%" className="text-center py-3">
                                        <strong>Agrega las construcciones omitidas a dar de alta.</strong>
                                    </td>
                                </tr>
                            ) : (Object.entries(newConstructions).map(([constructionId, data], index, array) => {
                                    const className = `py-3 px-5 ${
                                        index === array.length - 1 ? "" : "border-b border-blue-gray-50"
                                    }`;

                                    return (
                                        <tr key={constructionId}>
                                            <td className={className}>
                                                <select
                                                    value={data.construction_value_id || "0"}
                                                    onChange={(e) => handleNewConstructionChange(constructionId, 'construction_value_id', e.target.value)}
                                                    className="w-full rounded-[7px] border-blue-gray-200"
                                                >
                                                    <option key="0" value="0">SELECCIONA UN TIPO</option>
                                                    {constValues.map((constValue) => (
                                                        <option key={constValue.id}
                                                                value={constValue.id}>
                                                            {constValue.id} - {constValue.description}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className={className}>
                                                <select
                                                    value={data.construction_devaluation_id}
                                                    onChange={(e) => handleNewConstructionChange(constructionId, 'construction_devaluation_id', e.target.value)}
                                                    className="w-full rounded-[7px] border-blue-gray-200"
                                                >
                                                    {constDevaluations.map((constDevaluation) => (
                                                        <option
                                                            key={constDevaluation.id}
                                                            value={constDevaluation.id}>
                                                            {constDevaluation.id}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className={className}>
                                                <Input
                                                    value={data.area || 0}
                                                    onChange={(e) => handleNewConstructionChange(constructionId, 'area', e.target.value)}
                                                    containerProps={{className: "!min-w-0"}}
                                                />
                                            </td>
                                            <td className={className}>
                                                <Button
                                                    variant="text"
                                                    className="text-red-500 px-2 py-2"
                                                    onClick={() => removeConstruction(constructionId)}
                                                >
                                                    Quitar
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                            </tbody>
                            <tfoot>
                            <tr className='border-t'>
                                <td colSpan='2'
                                    className='py-3 px-5 text-right border-r'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Total nuevo
                                    </Typography>
                                </td>
                                <td colSpan='2' className='py-3 px-5 bg-gray-50 border-b'>
                                    <NumericFormat
                                        value={totalNewArea}
                                        displayType={'text'}
                                        thousandSeparator=","
                                    /> m<sup>2</sup>
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <div className='py-2'>
                            <Typography
                                variant="small"
                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                            >
                                Fecha efecto
                            </Typography>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Input value={currentLot.effective_bimester}
                                           onChange={(e) => setCurrentLot({
                                               ...currentLot,
                                               effective_bimester: e.target.value,
                                           })}
                                           label={'Bim'}
                                           containerProps={{
                                               className: '!min-w-0'
                                           }}
                                    />
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Input value={currentLot.effective_year}
                                           onChange={(e) => setCurrentLot({
                                               ...currentLot,
                                               effective_year: e.target.value,
                                           })}
                                           label={'Año'}
                                           containerProps={{
                                               className: '!min-w-0'
                                           }}
                                    />
                                </div>
                            </div>
                        </div>
                        <hr className="my-6 border-b-1 border-blue-gray-100"/>
                        <h6 className="text-blue-gray-300 text-sm mt-3 mb-6 font-bold uppercase">
                            NOTAS DE MOVIMIENTOS DEL PREDIO
                        </h6>
                        <div className="flex flex-wrap">
                            <div className="w-full lg:w-12/12 px-0">
                                <div className="relative w-full mb-3">
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                    >
                                        Notas
                                    </Typography>
                                    <textarea
                                        type="text"
                                        className="border-0 px-3 py-3 placeholder-blueGray-300 text-blue-gray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                                        value={currentLot.notes || ""}
                                        onChange={(e) => setCurrentLot({
                                            ...currentLot,
                                            notes: e.target.value,
                                        })}
                                        rows="6"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <Button
                            className="w-full bg-green-500 mt-4 hover:bg-green-600 text-white rounded-md px-5 py-3 font-semibold"
                            onClick={() => handleSubmit()}
                            disabled={loaderActive}
                        >
                            GUARDAR
                        </Button>
                        {
                            error && <p style={{color: "red"}}>{error}</p>
                        }
                    </CardBody>
                </Card>
            </div>
            <Dialog open={openEndDialog} handler={setOpenEndDialog}>
                <DialogHeader>Guardado</DialogHeader>
                <DialogBody>
                    <Typography>
                        Se dio de alta la construcción del predio
                    </Typography>
                </DialogBody>
                <DialogFooter>
                    <Button
                        className="bg-green-500 text-white"
                        onClick={() => handleReset()}
                        disabled={loaderActive}
                    >
                        Finalizar
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    )
};

export default ConstructionAddition;
